import { isBrowser } from "Shared/device-type";
import * as sessionStorage from "Shared/session-storage";
import * as server from "Shared/server";
import { parseUrl, urlToString, areEqual, isUrlObject, currentUrl,
// isExternal,
 } from "Shared/url";
import { scrollPosition } from "SiteLayout/Root";
let listeners = [];
export const historyAPISupported = (function () {
    if (!isBrowser()) {
        return false;
    }
    const ua = navigator.userAgent;
    if ((ua.indexOf("Android 2.") !== -1 || ua.indexOf("Android 4.0") !== -1) &&
        ua.indexOf("Mobile Safari") !== -1 &&
        ua.indexOf("Chrome") === -1 &&
        ua.indexOf("Windows Phone") === -1) {
        return false;
    }
    // Don't use History API for edit mode, this to make on page editing work
    if (window.IS_IN_EDIT_MODE) {
        return false;
    }
    return window.history && "pushState" in window.history;
})();
let ignoreNextPopStateEvent = false;
const deserialized = deserialize();
const historyStates = deserialized.historyStates;
let currentHistoryIndex = deserialized.currentHistoryIndex;
serialize();
function makeHashUrlForApp(url) {
    // if (IS_APP) {
    //   if (isExternal(url)) {
    //     throw new Error(
    //       "External url navigation in app must be explicitly handled"
    //     );
    //   }
    //   const urlString = urlToString(Object.assign({}, url, { hostname: null }));
    //   return "#!" + urlString;
    // } else {
    //   return urlToString(url);
    // }
    return urlToString(url);
}
export function pushState(url = undefined, options = {}) {
    let urlObject = null;
    if (!url) {
        urlObject = currentUrl();
    }
    else if (typeof url === "string") {
        urlObject = parseUrl(normalizeUrl(url));
    }
    else if (isUrlObject(url)) {
        urlObject = url;
    }
    else {
        throw new Error("Unknown type for `url` passed to pushState: " +
            typeof url +
            " " +
            JSON.stringify(url));
    }
    if (historyAPISupported) {
        const previousState = historyStates[currentHistoryIndex];
        if (previousState) {
            previousState.scrollPosition = scrollPosition();
        }
        const state = {
            stateId: stateId(),
            // We clear `hiddenQuery` here because when we get a `popstate` event
            // we don't want the `hiddenQuery`. The hidden query string is used
            // to pass information to the server to only load a partial page
            // and if we go back/forward to that state, we want the full page.
            url: Object.assign({}, urlObject, {
                hiddenQuery: {},
            }),
        };
        // If the user pressed Back a couple of times and then click
        // on a link, we splice off the states that she backed away from
        historyStates.splice(currentHistoryIndex + 1);
        historyStates.push(state);
        currentHistoryIndex++;
        serialize();
        window.history.pushState(state, "", urlToString(urlObject));
        return triggerEvent({
            event: "push",
            url: urlObject,
            state,
            previousState,
            options,
            revert() {
                if (historyStates[currentHistoryIndex] !== state) {
                    console.debug("Ignoring revert of history state change because the state has changed");
                    return () => false;
                }
                currentHistoryIndex--;
                serialize();
                back(true);
                return () => {
                    if (previousState !== historyStates[currentHistoryIndex]) {
                        console.debug("Ignoring replay of history state change because the state has changed");
                        return false;
                    }
                    console.debug("Retrying reverted push state change to", urlObject);
                    pushState(url, options);
                    return true;
                };
            },
        });
    }
    else {
        window.location.href = urlToString(urlObject);
    }
}
export function replaceState(url = undefined, options = {}, dispatchEvent = true) {
    let urlObject = null;
    if (!url) {
        urlObject = currentUrl();
    }
    else if (typeof url === "string") {
        urlObject = parseUrl(normalizeUrl(url));
    }
    else if (isUrlObject(url)) {
        urlObject = url;
    }
    else {
        throw new Error("Unknown type for `url` passed to replaceState: " +
            typeof url +
            " " +
            JSON.stringify(url));
    }
    const previousState = historyStates[currentHistoryIndex];
    const state = {
        stateId: stateId(),
        url: Object.assign({}, urlObject, {
            hiddenQuery: {},
        }),
        scrollPosition: scrollPosition(),
    };
    historyStates[currentHistoryIndex] = state;
    if (historyAPISupported) {
        window.history.replaceState(state, "", makeHashUrlForApp(urlObject));
    }
    serialize();
    // If `replaceState` is called with `dispatchEvent` === false it means that the url bar
    // is not in sync with what the server gave us (the server redirected). So we call replaceState
    // to get back in sync but we don't want to dispatch any events because that would cause us to
    // reload the page which we don't need.
    if (!dispatchEvent) {
        return;
    }
    return triggerEvent({
        event: "replace",
        url: urlObject,
        state,
        previousState,
        options,
        revert() {
            if (historyStates[currentHistoryIndex] !== state) {
                console.debug("Ignoring revert of history state change because the state has changed");
                return () => false;
            }
            if (previousState) {
                historyStates[currentHistoryIndex] = previousState;
                if (historyAPISupported) {
                    window.history.replaceState(previousState, "", urlToString(previousState.url));
                }
                serialize();
            }
            return () => {
                if (previousState !== historyStates[currentHistoryIndex]) {
                    console.debug("Ignoring replay of history state change because the state has changed");
                    return false;
                }
                console.debug("Retrying reverted replace state change to", urlObject);
                replaceState(url, options);
                return true;
            };
        },
    });
}
export function on(events, callback) {
    let eventList = null;
    if (typeof events === "string") {
        eventList = [events];
    }
    else {
        eventList = events;
    }
    eventList.forEach((eventName) => listeners.push({ event: eventName, callback }));
    // We return a function here that you can call to unscribe to the event.
    // This instead of exporting an `off` function where you have to pass
    // the same function you passed to `on` which makes it impossible to
    // pass an inline function to `on`.
    return () => eventList.forEach((eventName) => (listeners = listeners.filter((l) => !(l.callback === callback && l.event === eventName))));
}
export function one(event, callback) {
    const unsubscribe = on(event, (e) => {
        callback(e);
        unsubscribe();
    });
}
export function back(skipPopEvent = false) {
    ignoreNextPopStateEvent = skipPopEvent;
    window.history.back();
}
export function forward(skipPopEvent = false) {
    ignoreNextPopStateEvent = skipPopEvent;
    window.history.forward();
}
export function go(index, skipPopEvent = false) {
    ignoreNextPopStateEvent = skipPopEvent;
    window.history.go(index);
}
export function captureLinks() {
    // Don't capture any links when in edit mode. We want full page loads in edit mode
    // to make on page editing work
    if (window.IS_IN_EDIT_MODE) {
        return;
    }
    document.addEventListener("click", (e) => {
        let defaultPrevented = false;
        const link = clickedLink(e);
        if (!link) {
            return;
        }
        const ticket = link.getAttribute("data-ticket");
        const useReplaceState = link.getAttribute("data-replace");
        if ((/^http[s]?:/.test(link.href) || /^file:\/\/\//.test(link.href)) &&
            isNormalClick(e) &&
            !isExternalLink(link)) {
            e.preventDefault();
            e.preventDefault = () => (defaultPrevented = true);
        }
        else {
            // This is a special case where the user opens a link in a new tab. We can't set
            // headers for that request, so we need to send it as a special request
            if (isOpenInTabClick(e) && ticket) {
                server.post("/esales/notifyclick", { ticket });
            }
            return;
        }
        // Not very elegant to schedule a timeout here which is a macro task.
        // If we could schedule a micro task instead, we could call e.preventDefault()
        // in that micro task but it's too late to do that in a macro task.
        // When micro task scheduling API's are available in the browsers we support
        // (like Promises) we can rewrite this.
        const push = () => {
            // If any listener called e.preventDefault() it's because
            // the listener doesn't want to change the url when clicking
            // on a link, and we should respect that here.
            if (defaultPrevented) {
                return;
            }
            const options = {
                ticket,
                skipPartialCache: !!link.getAttribute("data-skip-partial-cache"),
            };
            if (link && !isExternalLink(link)) {
                if (areEqual(currentUrl(), link.href) || useReplaceState) {
                    replaceState(link.href, options);
                }
                else {
                    pushState(link.href, options);
                }
            }
        };
        // Since the components uses Reacts event system and we use a capturing event listener here
        // we wait for the components click handler to run if it may want to prevent default.
        // The reason for not running setTimeout in all cases is because some older devices give
        // an intermittent noticable delay for clicks.
        if (link.getAttribute("data-may-prevent-default")) {
            setTimeout(push, 0);
        }
        else {
            push();
        }
    }, true);
}
function isNormalClick(e) {
    return !(e.which > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);
}
function isOpenInTabClick(e) {
    return ((e.which === 1 && e.ctrlKey) ||
        (e.which === 1 && e.metaKey) ||
        (e.which === 2 && !e.ctrlKey && !e.metaKey));
}
function isExternalLink(link) {
    // In IE `link.hostname` equals an empty string if the location is relative
    return (!(link.hostname === "" || link.hostname === window.location.hostname) ||
        link.target === "_blank");
}
function clickedLink(e) {
    let target = e.target;
    do {
        if (target.tagName === "A") {
            return target;
        }
    } while ((target = target.parentElement));
    return null;
}
if (isBrowser()) {
    window.addEventListener("popstate", (e) => {
        // When we revert a state change, we need to call `back()` which triggers
        // a `popstate` which we want to ignore
        if (ignoreNextPopStateEvent) {
            ignoreNextPopStateEvent = false;
            return;
        }
        // Safari triggers a popstate on load which we want to ignore
        if (!e.state || !e.state.stateId) {
            return;
        }
        const previousState = historyStates[currentHistoryIndex];
        if (previousState) {
            previousState.scrollPosition = scrollPosition();
        }
        const stateIndex = historyStates.findIndex((s) => s.stateId === e.state.stateId);
        // We find the state object in `historyStates` here instead of using `e.state`
        // directly because we might have modified (added scrollPosition) the object
        // after we gave it to pushState/replaceState.
        let eventState = historyStates[stateIndex];
        if (process.env.NODE_ENV !== "production") {
            if (!eventState) {
                console.error("historyStates", historyStates, "e", e, "currentUrl()", currentUrl());
                throw new Error("`eventState` is not defined.");
            }
        }
        // This can happen if the user is in private browsing mode on iOS
        // In that case nothing will get serailized and `historyStates` will
        // always be empty for when the page loads but we can still get
        // state objects from the native history API.
        if (!eventState) {
            eventState = e.state;
            historyStates.push(eventState);
            currentHistoryIndex = historyStates.length - 1;
        }
        const previousHistoryIndex = currentHistoryIndex;
        const isBack = stateIndex < currentHistoryIndex;
        currentHistoryIndex = stateIndex;
        let event;
        if (isBack) {
            event = "pop";
        }
        else {
            event = "push";
        }
        triggerEvent({
            url: eventState.url,
            event,
            state: eventState,
            restoreState: true,
            previousState,
            revert() {
                if (eventState !== historyStates[currentHistoryIndex]) {
                    console.debug("Ignoring revert of history state change because the state has changed");
                    return () => false;
                }
                const revertToIndex = previousHistoryIndex - stateIndex;
                const revertToState = historyStates[revertToIndex];
                go(revertToIndex, true);
                currentHistoryIndex = revertToIndex;
                serialize();
                return () => {
                    if (revertToState !== historyStates[currentHistoryIndex]) {
                        console.debug("Ignoring revert of history state change because the state has changed");
                        return false;
                    }
                    console.debug("Retrying reverted state change to", eventState.url);
                    go(stateIndex - previousHistoryIndex);
                    return true;
                };
            },
        });
        serialize();
    });
    window.addEventListener("beforeunload", (e) => {
        if (historyStates[currentHistoryIndex]) {
            historyStates[currentHistoryIndex].scrollPosition = scrollPosition();
            serialize();
        }
    });
}
function triggerEvent(data) {
    const promises = [];
    listeners.forEach((o) => {
        if (o.event === data.event) {
            const maybePromise = o.callback(data);
            if (maybePromise instanceof Promise) {
                promises.push(maybePromise);
            }
        }
    });
    return Promise.all(promises);
}
function stateId() {
    return Math.ceil(Math.random() * 100000000);
}
const urlNormalizer = isBrowser() ? document.createElement("a") : null;
function normalizeUrl(url) {
    urlNormalizer.href = url;
    let normalizedUrl = urlNormalizer.pathname + urlNormalizer.search + urlNormalizer.hash;
    if (normalizedUrl.indexOf("/") !== 0) {
        // Don't we love IE... link.pathname doesn't start with slash in IE
        normalizedUrl = "/" + normalizedUrl;
    }
    return normalizedUrl;
}
function serialize() {
    if (!isBrowser()) {
        return;
    }
    sessionStorage.set("historyStates", historyStates);
    sessionStorage.set("currentHistoryIndex", currentHistoryIndex);
}
function deserialize() {
    if (!isBrowser()) {
        return {
            historyStates: [],
            currentHistoryIndex: 0,
        };
    }
    let historyStates = sessionStorage.get("historyStates", createInitalHistoryState());
    let currentHistoryIndex = sessionStorage.get("currentHistoryIndex") || 0;
    if (history.state) {
        currentHistoryIndex = historyStates.findIndex((s) => s.stateId === history.state.stateId);
        if (currentHistoryIndex === -1) {
            currentHistoryIndex = 0;
            if (process.env.NODE_ENV !== "production") {
                console.error("history.state contained a state not present in serialized state list");
            }
        }
    }
    let state = historyStates[currentHistoryIndex];
    if (!state) {
        const error = new Error("currentHistoryIndex and historyStates are not in sync, " +
            "currentHistoryIndex: " +
            currentHistoryIndex +
            ", " +
            "history.state: " +
            JSON.stringify(history.state) +
            ", " +
            "historyStates: " +
            JSON.stringify(historyStates));
        if (process.env.NODE_ENV !== "production") {
            throw error;
        }
        else {
            window.newrelic && window.newrelic.noticeError(error);
            // If the state is out of sync because an error occured on another page
            // we first try to repair it
            const index = historyStates.findIndex((s) => areEqual(s.url, currentUrl()));
            if (index !== -1) {
                currentHistoryIndex = index;
                state = historyStates[currentHistoryIndex];
            }
            else {
                historyStates = createInitalHistoryState();
                currentHistoryIndex = 0;
                state = historyStates[currentHistoryIndex];
            }
        }
    }
    if (!areEqual(state.url, currentUrl())) {
        if (process.env.NODE_ENV !== "production") {
            console.debug("adding new history state after manually navigating to new url");
        }
        currentHistoryIndex++;
        historyStates.splice(currentHistoryIndex, historyStates.length - currentHistoryIndex, {
            url: currentUrl(),
            stateId: stateId(),
        });
    }
    // We call replaceState() here to get a stateId for the initial state as well
    // This is mostly so we can ignore the `popstate` event that Safari fires on
    // page load that doesn't contain any state.
    if (historyAPISupported) {
        window.history.replaceState(historyStates[currentHistoryIndex], "", urlToString(currentUrl()));
    }
    return {
        historyStates,
        currentHistoryIndex,
    };
}
function createInitalHistoryState() {
    return [
        {
            url: currentUrl(),
            stateId: history.state ? history.state.stateId : stateId(),
        },
    ];
}
